import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { GitCompareArrows, RefreshCw, X, TrendingUp, TrendingDown, Minus, Plus, Circle, Sparkles } from "lucide-react";

/** Iter 175 · Snapshot-vs-latest diff view.
 *  Shows every field that changed vs. the saved baseline, colour-coded
 *  green (improved), red (regressed), amber (changed), gray (same). */

const TONE = {
  same: "text-muted-foreground",
  changed: "bg-amber-50 text-amber-800 border-amber-300",
  improved: "bg-emerald-50 text-emerald-800 border-emerald-300",
  regressed: "bg-rose-50 text-rose-800 border-rose-300",
  added: "bg-sky-50 text-sky-800 border-sky-300",
  removed: "bg-slate-50 text-slate-500 border-slate-300",
};
const ICON = {
  same: <Minus className="w-3 h-3" />,
  improved: <TrendingUp className="w-3 h-3" />,
  regressed: <TrendingDown className="w-3 h-3" />,
  changed: <Circle className="w-3 h-3" />,
  added: <Plus className="w-3 h-3" />,
  removed: <X className="w-3 h-3" />,
};

const HIDE_STATUSES = new Set(["same"]);

function summary(row) {
  if ("saved_count" in row) {
    return `${row.saved_count} → ${row.live_count}`;
  }
  const shorten = (s) => {
    if (s === null || s === undefined) return "—";
    if (typeof s !== "string") return String(s);
    const cleaned = s.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
    return cleaned.length > 80 ? cleaned.slice(0, 77) + "…" : (cleaned || "—");
  };
  return `${shorten(row.saved)} → ${shorten(row.live)}`;
}

function BrandDiffCard({ diff }) {
  const [showAll, setShowAll] = useState(false);
  if (!diff.has_baseline) {
    return (
      <div className="border border-dashed border-border p-4 text-xs text-muted-foreground"
             data-testid={`diff-brand-${diff.brand_id}-no-baseline`}>
        <b>{diff.brand_name}</b> — no baseline captured. Legacy snapshot with
        no saved state. Use <b>Capture current state</b> to establish a new
        baseline going forward.
      </div>
    );
  }
  const fields = diff.fields || {};
  const entries = Object.entries(fields).filter(([, r]) => r && r.status);
  const visible = showAll ? entries
    : entries.filter(([, r]) => !HIDE_STATUSES.has(r.status));
  const counts = entries.reduce((acc, [, r]) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="border border-border bg-card" data-testid={`diff-brand-${diff.brand_id}`}>
      <div className="px-3 py-2 border-b border-border flex items-center gap-2 flex-wrap">
        <b className="text-sm">{diff.brand_name}</b>
        <span className="text-[10px] text-muted-foreground ml-auto">
          {(counts.improved || 0) > 0 && <span className="text-emerald-700 mr-2"><TrendingUp className="w-3 h-3 inline" /> {counts.improved}</span>}
          {(counts.regressed || 0) > 0 && <span className="text-rose-700 mr-2"><TrendingDown className="w-3 h-3 inline" /> {counts.regressed}</span>}
          {(counts.changed || 0) > 0 && <span className="text-amber-700 mr-2"><Circle className="w-3 h-3 inline" /> {counts.changed}</span>}
          {(counts.added || 0) > 0 && <span className="text-sky-700 mr-2"><Plus className="w-3 h-3 inline" /> {counts.added}</span>}
          {(counts.removed || 0) > 0 && <span className="text-slate-500 mr-2"><X className="w-3 h-3 inline" /> {counts.removed}</span>}
          {(counts.same || 0) > 0 && <span className="text-muted-foreground">{counts.same} unchanged</span>}
        </span>
        <button type="button" className="text-[10px] text-muted-foreground hover:text-foreground"
                  onClick={() => setShowAll((s) => !s)}
                  data-testid={`diff-brand-${diff.brand_id}-toggle-all`}>
          {showAll ? "Hide unchanged" : "Show all"}
        </button>
      </div>
      <table className="min-w-full text-xs">
        <tbody>
          {visible.map(([path, row]) => (
            <tr key={path} className="border-b border-border last:border-b-0">
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-muted-foreground w-48">{path}</th>
              <td className="px-3 py-2">
                <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${TONE[row.status] || ""}`}>
                  {ICON[row.status]}{row.status}
                </span>
              </td>
              <td className="px-3 py-2 text-foreground/90">{summary(row)}</td>
            </tr>
          ))}
          {visible.length === 0 && (
            <tr><td colSpan={3} className="px-3 py-6 text-center text-muted-foreground text-xs">
              Nothing has changed vs. the baseline. Nice consistency.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function BrandCompareDiffDialog({ wsId, snapshotId, snapshotName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const [summaryCached, setSummaryCached] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(
        `/workspaces/${wsId}/brand-compare/snapshots/${snapshotId}/diff`);
      setData(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load diff"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [snapshotId]);

  const recapture = async () => {
    if (!confirm("Overwrite the saved baseline with today's brand state? Old diffs will use the new baseline.")) return;
    setCapturing(true);
    try {
      await api.post(
        `/workspaces/${wsId}/brand-compare/snapshots/${snapshotId}/capture-state`);
      toast.success("Baseline updated");
      setSummary(null);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Capture failed")); }
    finally { setCapturing(false); }
  };

  const generateSummary = async (force = false) => {
    setSummarizing(true);
    const t = toast.loading(force ? "Regenerating AI summary…" : "Generating AI summary…");
    try {
      const r = await api.post(
        `/workspaces/${wsId}/brand-compare/snapshots/${snapshotId}/diff-summary`,
        null, { params: force ? { force: true } : {} });
      setSummary(r.data.summary);
      setSummaryCached(!!r.data.cached);
      toast.success(r.data.cached ? "Loaded cached summary" : "Summary ready", { id: t });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Summary failed"), { id: t });
    } finally { setSummarizing(false); }
  };

  const copySummary = async () => {
    if (!summary) return;
    const text = [
      summary.headline,
      "",
      ...summary.bullets.map((b) => `• ${b}`),
      ...(summary.regressions?.length ? ["", "Regressions:", ...summary.regressions.map((r) => `⚠ ${r}`)] : []),
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Summary copied");
    } catch { toast.error("Could not copy"); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col p-0"
                        data-testid="brand-compare-diff-dialog">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-border">
          <div className="flex items-center gap-2">
            <DialogTitle className="flex items-center gap-2">
              <GitCompareArrows className="w-5 h-5" />
              Diff vs. saved · {snapshotName}
            </DialogTitle>
            <div className="ml-auto flex gap-2">
              <Button variant="outline"
                        className="rounded-none h-8 border-violet-500/50 text-violet-700 hover:bg-violet-50"
                        onClick={() => generateSummary(false)} disabled={summarizing}
                        data-testid="brand-compare-diff-summary-btn">
                <Sparkles className={`w-3.5 h-3.5 mr-1.5 ${summarizing ? "animate-pulse" : ""}`} />
                {summarizing ? "Summarizing…" : "AI summary"}
              </Button>
              <Button variant="outline" className="rounded-none h-8" onClick={recapture} disabled={capturing}
                        data-testid="brand-compare-diff-recapture">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${capturing ? "animate-spin" : ""}`} />
                {capturing ? "Updating…" : "Update baseline"}
              </Button>
              <Button variant="ghost" className="rounded-none h-8 w-8 p-0" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {summary && (
            <div className="border-2 border-violet-300 bg-violet-50/40 p-4"
                   data-testid="brand-compare-diff-summary-panel">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-violet-700" />
                <span className="text-[10px] uppercase tracking-widest text-violet-700 font-semibold">
                  Executive summary{summaryCached ? " · cached" : ""}
                </span>
                <span className="text-[10px] text-muted-foreground ml-2">
                  confidence {summary.confidence}%
                </span>
                <div className="ml-auto flex gap-1">
                  <button type="button" onClick={copySummary}
                            className="text-[10px] px-2 py-0.5 border border-violet-300 hover:bg-violet-100"
                            data-testid="brand-compare-diff-summary-copy">Copy</button>
                  <button type="button" onClick={() => generateSummary(true)}
                            disabled={summarizing}
                            className="text-[10px] px-2 py-0.5 border border-violet-300 hover:bg-violet-100 disabled:opacity-50"
                            data-testid="brand-compare-diff-summary-regen">Regenerate</button>
                </div>
              </div>
              {summary.headline && (
                <div className="font-semibold text-sm mb-2" data-testid="brand-compare-diff-summary-headline">
                  {summary.headline}
                </div>
              )}
              <ul className="list-disc pl-5 space-y-1 text-sm" data-testid="brand-compare-diff-summary-bullets">
                {summary.bullets.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
              {summary.regressions?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-violet-300">
                  <div className="text-[10px] uppercase tracking-widest text-rose-700 font-semibold mb-1">
                    Regressions to review
                  </div>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-rose-800"
                        data-testid="brand-compare-diff-summary-regressions">
                    {summary.regressions.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">Loading diff…</div>
          ) : !data ? null : (data.diffs || []).map((d) => (
            <BrandDiffCard key={d.brand_id} diff={d} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
